/* eslint-disable valid-jsdoc */
// Utility Functions
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll'

export const isIOS =
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
let savedScrollY = 0

/**
 * Создаёт debounced версию функции, которая задерживает выполнение до тех пор,
 * пока не пройдёт указанное количество миллисекунд после последнего вызова
 *
 * @param {T} func - Функция для debounce
 * @param {number} wait - Время ожидания в миллисекундах
 * @return {any} Debounced функция
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, space-before-function-paren
export function debounce<T extends (..._args: any[]) => any>(
  func: T,
  wait: number
): (..._args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}

/**
 * Создаёт throttled версию функции, которая может вызываться не чаще,
 * чем один раз в указанный интервал времени
 *
 * @param {T} func - Функция для throttle
 * @param {number} limit - Лимит времени в миллисекундах
 * @return {any} Throttled функция
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, space-before-function-paren
export function throttle<T extends (..._args: any[]) => any>(
  func: T,
  limit: number
): (..._args: Parameters<T>) => void {
  let inThrottle: boolean

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * Проверяет, находится ли элемент в зоне видимости вьюпорта
 *
 * @param {HTMLElement} element - HTML элемент для проверки
 * @return {any} true, если элемент виден
 */
export function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

/**
 * Получает текущую точку перелома (breakpoint) на основе ширины окна
 *
 * @return {any} Название breakpoint: 'mobile', 'tablet', 'desktop' или 'large'
 */
export function getCurrentBreakpoint(): string {
  const width = window.innerWidth

  if (width <= 768) return 'mobile'
  if (width <= 1024) return 'tablet'
  if (width <= 1240) return 'desktop'
  return 'large'
}

/**
 * Ожидает появления элемента в DOM с использованием MutationObserver
 *
 * @param {string} selector - CSS селектор элемента
 * @param {number} timeout - Время ожидания в миллисекундах (по умолчанию 5000)
 * @return {any} Promise, разрешающийся с найденным элементом или отклоняющийся по таймауту
 */
export function waitForElement(
  selector: string,
  timeout = 5000
): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    const element = document.querySelector<HTMLElement>(selector)

    if (element) {
      resolve(element)
      return
    }

    const observer = new MutationObserver(() => {
      const el = document.querySelector<HTMLElement>(selector)
      if (el) {
        observer.disconnect()
        resolve(el)
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    setTimeout(() => {
      observer.disconnect()
      reject(new Error(`Element ${selector} not found within ${timeout}ms`))
    }, timeout)
  })
}

/**
 * Отключает прокрутку страницы, с учетом особенностей iOS устройств
 */
export function disableScroll(): void {
  disablePageScroll()

  if (isIOS) {
    savedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollY}px`
    document.body.style.width = '100%'
  }
}

/**
 * Включает прокрутку страницы, с учетом особенностей iOS устройств
 */
export function enableScroll(): void {
  enablePageScroll()

  if (isIOS) {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo({ top: savedScrollY, behavior: 'instant' })
  }
}

/**
 * Возвращает правильную форму слова в зависимости от числа
 * @param {number} count - Количество
 * @param {string[]} forms - Массив форм слова: [единственное, двойственное, множественное]
 * @return {string} Правильная форма слова
 */
export function pluralizeWord(
  count: number,
  forms: [string, string, string]
): string {
  if (count % 10 === 1 && count % 100 !== 11) return forms[0]
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return forms[1]
  return forms[2]
}
